import * as React from 'react';
import { CommunicationAcceptanceItem } from './types/CommunicationAcceptanceItem';
import { UpdatedCommunicationAcceptanceVM } from './types/UpdatedCommunicationAcceptanceVM';
import { InitialData } from './types/InitialData';
import { TableConfigurator } from './helpers/TableConfigurator';
import Status from '../../../core/types/Status';
import {
  Alert,
  Button,
  Form,
  Loader,
  ModuleContainer,
  SelectField,
  Table,
} from '../../../core/components';
import { DataTable, emptyDataTable } from '../../../core/components/Table/types/DataTable';
import { InfoMessageService, TranslationService } from '../../../core/services';
import ApiProvider from '../../../core/api/ApiProvider';
import { Field, FieldAttributes } from 'formik';
import { useState } from 'react';
import { TenantAccountType } from '../../types/TenantAccountType';
import useEffectAsync from '../../../core/hooks/useEffectAsync';

const CommunicationAcceptance = () => {
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [isFirstLoading, setIsFirstLoading] = React.useState<boolean>(true);
  const [isSaveAcceptanceEnabled, setIsSaveAcceptanceEnabled] = React.useState<boolean>(true);
  const [model, setModel] = React.useState<DataTable<CommunicationAcceptanceItem>>({
    ...emptyDataTable,
  });
  const [tenantDebtors, setTenantDebtors] = useState<TenantAccountType[]>([]);

  const fetchCommunicationAcceptables = async (tenantDebtor?: TenantAccountType) => {
    try {
      setIsLoading(true);

      const path = `CommunicationAcceptanceModule/GetUserCommunicationAcceptances`;

      const data = await ApiProvider.get<Status<InitialData>>(path);

      setModel(data.data.data);
      setIsSaveAcceptanceEnabled(data.data.isSaveAcceptanceEnabled);
      setTenantDebtors(data.data.tenantDebtors);
    } finally {
      setIsFirstLoading(false);
      setIsLoading(false);
    }
  };

  const onClick = async () => {
    try {
      setIsLoading(true);
      const updateData = model.dataRows.map((item) => ({
        AcceptanceId: item.acceptanceId,
        IsAccepted: item.isAcceptanceGiven,
      }));

      const data = await ApiProvider.post<Status<UpdatedCommunicationAcceptanceVM>>(
        `CommunicationAcceptanceModule/UpdateCommunicationAcceptances`,
        updateData
      );
      InfoMessageService.success(TranslationService.translate('GeneralSuccessMsg'));
      if (data.success && data.data.IsRedirectRequired && data.data.RedirectUrl !== null) {
        window.location.href = data.data.RedirectUrl;
      } else if (!data.success) {
        InfoMessageService.error(TranslationService.translate('GeneralErrorMsg'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onAcceptanceChange = (item: CommunicationAcceptanceItem) => {
    const table = { ...model };
    const rows = [...table.dataRows];
    const index = rows.findIndex((x) => x.acceptanceId === item.acceptanceId);
    rows[index] = { ...item, isAcceptanceGiven: !item.isAcceptanceGiven };
    table.dataRows = [...rows];
    setModel(table);
  };

  const onUpdateTenantDebtor = async (tenantDebtor: TenantAccountType) => {
    await fetchCommunicationAcceptables(tenantDebtor);
  };

  useEffectAsync(async () => {
    await fetchCommunicationAcceptables();
  }, []);

  return (
    <ModuleContainer classNames="mt-2">
      <ModuleContainer.Header title={TranslationService.translate('CommunicationAcceptance')} />
      <ModuleContainer.Body variant={ModuleContainer.Body.BodyVariant.Primary}>
        {isLoading && <Loader opacity={0.4} />}

        {!isFirstLoading && (
          <Alert variant={Alert.Variants.Info} icon={true} className="mb-2">
            <>
              {TranslationService.translate('CommunicationAcceptanceInfo1')}
              <a
                href={TranslationService.translate('PersonalLinkValue')}
                target="_blank"
                rel="noreferrer"
                id="communication-acceptance-link"
              >
                {TranslationService.translate('PersonalInfoLink')}
              </a>
              {TranslationService.translate('CommunicationAcceptanceInfo2')}
            </>
          </Alert>
        )}

        <Table
          keyField="acceptanceId"
          headers={TableConfigurator.getTableColumns(model, onAcceptanceChange)}
          rows={model.dataRows}
          paginationProps={{
            hideSizePerPage: true,
            hidePageListOnlyOnePage: true,
          }}
        />

        {model.dataRows.length > 0 && isSaveAcceptanceEnabled && (
          <Button
            id="SaveAcceptance"
            type={Button.Types.Button}
            size={Button.Sizes.MD}
            variant={Button.Variants.Primary}
            onClick={onClick}
          >
            {TranslationService.translate('Save')}
          </Button>
        )}
      </ModuleContainer.Body>
    </ModuleContainer>
  );
};
export default CommunicationAcceptance;
